import { ProxyScribeClient as ScribeClient } from "@el8/scribe-client/dist/proxy-client";
import {
  type IBlobEvent,
  type IMediaRecorder,
  MediaRecorder,
} from "extendable-media-recorder";
import { useEffect } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  IS_CHROME_EXTENSION,
  NABLA_RECOMMENDED_RECORDING_SAMPLE_RATE_HZ,
  SCRIBE_RECORDER_TIMESLICE_MS,
} from "../constants";
import useEffectOnce from "../hooks/useEffectOnce";
import usePrevious from "../hooks/usePrevious";
import { scribeClientLogger } from "../logger";
import type { AiScribeStep } from "../types";
import { doesBrowserSupportDownsamplingAudio } from "../utils/browser";
import storage, { chromeStorage } from "../utils/storage";

function createAudioContext(): AudioContext {
  return new AudioContext({
    sampleRate: doesBrowserSupportDownsamplingAudio()
      ? NABLA_RECOMMENDED_RECORDING_SAMPLE_RATE_HZ
      : undefined,
  });
}

interface ScribeSetupOptions {
  getAuthToken: () => Promise<string>;
  websocketUrl: string;
}

function createScribeClient(
  options: ScribeSetupOptions,
  sampleRate: number,
): ScribeClient {
  return new ScribeClient({
    audio: {
      encoding: "pcm_s16le",
      sampleRate,
      timeslice: SCRIBE_RECORDER_TIMESLICE_MS,
    },
    auth: async () => {
      const authToken = await options.getAuthToken();

      console.log("authToken", authToken);
      return authToken;
    },
    drainStrategy: "speed-limit",
    // @ts-expect-error: scribe client logger levels vs @datadog/browser-logs
    logger: scribeClientLogger,
    target: options.websocketUrl,
    transport: "el8-transcribe",
  });
}

export interface ScribeStore {
  step: AiScribeStep;
  setStep: (step: AiScribeStep) => void;
  setMicrophoneDeviceId: (microphoneDeviceId: string | null) => void;
  getMediaDevices: () => Promise<MediaDeviceInfo[]>;
  setup: (options: ScribeSetupOptions) => Promise<void>;
  teardown: () => void;
  media: {
    mediaStream: MediaStream | null;
    microphoneDeviceId: string | null;
    mediaDevices: MediaDeviceInfo[] | null;
    audioContext: AudioContext;
    audioSource: MediaStreamAudioSourceNode | null;
    audioDestination: MediaStreamAudioDestinationNode | null;
    mediaRecorder: IMediaRecorder | null;
  };
  scribe: ScribeClient | null;
  scribingSessionId: string | undefined;
  setScribingSessionId: (scribingSessionId: string | undefined) => void;
}

export const scribeStore = create<ScribeStore>()(
  persist(
    (set, get) => ({
      step: "initial",
      media: {
        mediaStream: null,
        microphoneDeviceId: null,
        mediaDevices: null,
        audioContext: createAudioContext(),
        audioSource: null,
        audioDestination: null,
        mediaRecorder: null,
      },
      scribe: null,
      scribingSessionId: undefined,
      setScribingSessionId: (scribingSessionId) => {
        set({ scribingSessionId });
      },
      setup: async (options) => {
        const audioContext = createAudioContext();
        const microphoneDeviceId = get().media.microphoneDeviceId;

        if (!microphoneDeviceId) return;

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: microphoneDeviceId },
            echoCancellation: false,
          },
        });

        if (!mediaStream) return;

        const source = audioContext.createMediaStreamSource(mediaStream);
        const destination = audioContext.createMediaStreamDestination();

        source.connect(destination);

        const recorder = new MediaRecorder(destination.stream, {
          mimeType: "audio/wav",
        });

        const media = get().media;

        set({
          media: {
            ...media,
            mediaStream,
            audioContext,
            audioSource: source,
            audioDestination: destination,
            mediaRecorder: recorder,
          },
        });

        const client = createScribeClient(options, audioContext.sampleRate);

        set({ scribe: client });
      },
      teardown: () => {
        const state = get();
        const scribe = state.scribe;

        if (scribe) {
          scribe.removeAllListeners();
          scribe.terminate();

          set({ scribe: null, scribingSessionId: undefined });
        }

        const { media } = state;

        if (media.mediaRecorder) media.mediaRecorder.stop();

        media.audioDestination?.disconnect();
        media.audioSource?.disconnect();

        set({
          media: {
            ...media,
            mediaStream: null,
            audioSource: null,
            audioDestination: null,
            mediaRecorder: null,
          },
        });
      },
      setStep: (step) => {
        set({ step });
      },
      setMicrophoneDeviceId: (microphoneDeviceId) => {
        set({ media: { ...get().media, microphoneDeviceId } });
      },
      getMediaDevices: async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => {
          track.stop();
        });

        const mediaDevices = await navigator.mediaDevices.enumerateDevices();

        const audioInputDevices = mediaDevices.filter((device) => {
          return (
            device.deviceId &&
            device.kind === "audioinput" &&
            device.deviceId !== "default"
          );
        });

        set({ media: { ...get().media, mediaDevices: audioInputDevices } });

        return audioInputDevices;
      },
    }),
    {
      name: "scribe-store",
      storage: createJSONStorage(() =>
        IS_CHROME_EXTENSION ? chromeStorage : storage,
      ),
      partialize: (state) => ({
        media: {
          microphoneDeviceId: state.media.microphoneDeviceId,
          mediaDevices: [],
        },
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.media) {
          state.media.audioContext = createAudioContext();
        }
      },
    },
  ),
);

export function useMicrophoneDeviceId(): [
  microphoneDeviceId: string | null,
  setMicrophoneDeviceId: (microphoneDeviceId: string | null) => void,
] {
  const microphoneDeviceId = useScribeStore((s) => s.media.microphoneDeviceId);
  const setMicrophoneDeviceId = useScribeStore((s) => s.setMicrophoneDeviceId);

  return [microphoneDeviceId, setMicrophoneDeviceId];
}

export function useMediaStream(): [mediaStream: MediaStream | null] {
  const mediaStream = useScribeStore((s) => s.media.mediaStream);

  return [mediaStream];
}

export function useStep(): [
  step: AiScribeStep,
  setStep: (step: AiScribeStep) => void,
] {
  const step = useScribeStore((s) => s.step);
  const setStep = useScribeStore((s) => s.setStep);

  return [step, setStep];
}

export function useOnStepChange(
  config: Partial<Record<AiScribeStep, () => void>>,
) {
  const step = useScribeStore((s) => s.step);
  const prevStepRef = usePrevious(step);

  // biome-ignore lint/correctness/useExhaustiveDependencies: prevStepRef is used in the effect
  useEffect(() => {
    if (prevStepRef !== step) {
      config[step]?.();
    }
  }, [step, prevStepRef]);
}

export function useMediaDevices(): [
  mediaDevices: MediaDeviceInfo[] | null,
  getMediaDevices: () => Promise<MediaDeviceInfo[]>,
] {
  const media = useScribeStore((s) => s.media);
  const getMediaDevices = useScribeStore((s) => s.getMediaDevices);

  return [media.mediaDevices, getMediaDevices];
}

export function useAudioContext(): [audioContext: AudioContext] {
  const media = useScribeStore((s) => s.media);

  return [media.audioContext];
}

export function useAudioRecordingComponents(
  onDataAvailable: (this: IMediaRecorder, event: IBlobEvent) => Promise<void>,
) {
  const media = useScribeStore((s) => s.media);

  useEffect(() => {
    if (media.mediaRecorder) {
      media.mediaRecorder.ondataavailable = onDataAvailable;
    }
  }, [media.mediaRecorder, onDataAvailable]);

  return {
    audioSource: media.audioSource,
    audioDestination: media.audioDestination,
    mediaRecorder: media.mediaRecorder,
  };
}

/**
 * Owns setup/teardown. Passes options into setup() so media and scribe client
 * are created in one flow.
 */
export function useSetupScribe(options: ScribeSetupOptions | null) {
  const setup = useScribeStore((s) => s.setup);
  const teardown = useScribeStore((s) => s.teardown);
  const media = useScribeStore((s) => s.media);
  const getMediaDevices = useScribeStore((s) => s.getMediaDevices);
  const microphoneDeviceId = media.microphoneDeviceId;
  const devices = media.mediaDevices;

  useEffectOnce(() => {
    getMediaDevices();
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: setup/teardown are called in useEffectOnce
  useEffect(() => {
    if (!microphoneDeviceId || !devices?.length || !options) {
      teardown();
      return;
    }

    setup(options);

    return () => teardown();
  }, [microphoneDeviceId, devices, options]);
}

export function useTeardown(): () => void {
  const teardown = useScribeStore((s) => s.teardown);

  return teardown;
}

export function useScribeReady(): boolean {
  const media = useScribeStore((s) => s.media);

  return (
    (media.mediaDevices?.length ?? 0) > 0 && media.microphoneDeviceId !== null
  );
}

export const useScribeStore = scribeStore;
